<template>
  <div class="tracking-networks">
    <router-view v-slot="slotProps">
      <component :is="slotProps?.Component" @saved="onSaved" />
    </router-view>

    <layout-logged-frame :title="$t('networks')">
      <template #header-actions>
        <header-actions :actions="headerActions" />
      </template>

      <template #content>
        <NetworkFilters @filter="onFilter" @clearFilters="onClearFilters" id="network-filters">
          <template v-slot:phone-actions>
            <sort-by-select
              :columns="columns"
              :pagination="pagination"
              :on-update-pagination="onUpdatePagination"
            />
          </template>
        </NetworkFilters>
        <table-base
          has-phone-layout
          :on-add="addAction?.onClick"
          :add-label="addAction?.title"
          :columns="columns"
          :items="networks"
          :pages="totalPages"
          :pagination="pagination"
          :is-loading="isLoading"
          has-custom-items-template
          :on-select-item="selectNetwork"
          :on-update-pagination="onUpdatePagination"
          :sync-to-url="true"
          @init-pagination="onInitPagination"
          :on-change-page="onChangePage"
        >
          <template #item-cell="scope">
            <template v-if="scope.cell.column.value === 'platforms'">
              <div class="d-flex flex-wrap gap-6">
                <PlatformPanel
                  v-for="platform in scope.cell.item.platforms"
                  :key="platform.code"
                  :country="platform"
                />
              </div>
            </template>

            <template v-else>
              {{ displayColumnValue(scope.cell.item, scope.cell.column.value) }}
            </template>
          </template>
        </table-base>
      </template>
    </layout-logged-frame>
  </div>
</template>

<script>
  import { extractNestedProp, isStr } from '../../utils';
  import columns from '../../config/tracking/columnsNetworks';
  import PlatformPanel from '@/components/panels/Platform.vue';
  import NetworkFilters from '@/components/forms/filters/TrackingNetworks.vue';
import urlPaginationRestore from '@/mixins/urlPaginationRestore';

  export default {
    mixins: [urlPaginationRestore],
    components: {
      PlatformPanel,
      NetworkFilters,
    },
    data() {
      return {
        columns,
        pagination: {
          order: 'desc',
          page: 1,
          rowsPerPage: 30,
          sortBy: 'created_at',
          totalItems: 0,
        },
        totalPages: 0,
        query: {},
        isLoading: false,
      };
    },
    computed: {
      addAction() {
        return this.headerActions.find(({ key, isDisabled }) => key === 'add' && !isDisabled);
      },
      headerActions() {
        return [
          {
            key: 'add',
            title: this.$t('new_network'),
            onClick: this.openNewNetworkModal,
          },
        ];
      },
      networks() {
        return this.$store.getters['tracking/trafficNetworks'];
      },
    },
    methods: {
      // Emitted by the dialog routes after a successful save. Keeps the
      // refetch in this view, with this view's own query, and out of the URL.
      onSaved() {
        this.getNetworks(this.query);
      },
      async getNetworks(query = {}) {
        this.isLoading = true;
        query.per_page = this.pagination.rowsPerPage;

        return this.$store
          .dispatch('tracking/getTrafficNetworks', query)
          .then((res) => {
            const { meta } = res;
            if (meta) {
              const { total, current_page, last_page } = meta;
              this.pagination.page = current_page;
              this.pagination.totalItems = total;
              this.totalPages = last_page;
            }

            return res;
          })
          .catch((err) => err)
          .finally(() => {
            this.isLoading = false;
          });
      },
      onUpdatePagination({ page, order, sortBy }) {
        const { query } = this;
        this.pagination.order = order;
        this.pagination.sortBy = isStr(sortBy) ? sortBy : pagination.sortBy;
        const resolvedSort = isStr(sortBy) ? sortBy : null;
        const nextQuery = {
          ...query,
          page,
          ...(resolvedSort ? { sort: resolvedSort, order } : {}),
        };

        this.query = nextQuery;
        this.runInitialFetch(() => this.getNetworks(nextQuery));
      },
      onFilter(filters) {
        this.query = {
          ...this.query,
          ...filters,
          page: 1,
        };
        this.runInitialFetch(() => this.getNetworks(this.query));
      },
      onClearFilters() {
        const { sortBy, order } = this.pagination;
        this.query = { sort: sortBy, order, page: 1 };
        this.getNetworks(this.query);
      },
      selectNetwork(network) {
        this.$store.dispatch('tracking/setActiveTrafficNetwork', network);
        this.$router.push({ name: 'tracking-networks-detail', params: { id: network.id }, query: this.$route.query });
      },
      openNewNetworkModal() {
        this.$router.push({ name: 'tracking-networks-new', query: this.$route.query });
      },
      onChangePage(page) {
        const { query, pagination } = this;
        const { order, sortBy } = pagination;
        const nextQuery = {
          ...query,
          page,
          ...(sortBy ? { sort: sortBy, order } : {}),
        };

        this.query = nextQuery;
        this.getNetworks(nextQuery);
      },
      displayColumnValue(item, columnValue) {
        return extractNestedProp(item, columnValue);
      },
    },
  };
</script>

<style lang="scss">
</style>
